import React, {
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle
} from "react";
import {
  Box,
  MenuItem,
  Modal,
  Typography,
  Select,
  FormControl,
  InputLabel,
  Button,
  TextField,
} from "@mui/material";
import PendingActionsIcon from "@mui/icons-material/PendingActions";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CancelIcon from "@mui/icons-material/Cancel";
import axios from "axios";
import api,{ API_BASE_URL } from "../../utils/api";

// ForwardRef allows parent to trigger modal
const EditArchitecturalRequest = forwardRef(({ architecturalRequestDetails = {}, onUpdate }, ref) => {
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [updatedStatus, setUpdatedStatus] = useState("pending");
  const [comment, setComment] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [statuses, setStatuses] = useState([]);

  const currentUserId = localStorage.getItem("currentUserId") || 1;

  // Expose openModal to parent
  useImperativeHandle(ref, () => ({
    openModal: () => setIsEditModalOpen(true)
  }));

  // Fetch statuses
  useEffect(() => {
    const fetchStatuses = async () => {
      try {
        const response = await api.get(`/statuses/architectural`);
        setStatuses(response.data);
      } catch (error) {
        console.error("Error fetching architectural statuses:", error);
      }
    };
    fetchStatuses();
  }, []);

  // Set initial values when details change
  useEffect(() => {
    setUpdatedStatus(architecturalRequestDetails?.status ?? "pending");
    setComment(architecturalRequestDetails?.comment ?? "");
  }, [architecturalRequestDetails]);

  if (!architecturalRequestDetails || !architecturalRequestDetails.id) return null;

  const handleEditModalClose = () => setIsEditModalOpen(false);

  const handleSaveChanges = async () => {
    try {
      setIsSaving(true);
      const updatedData = {
        status: updatedStatus,
        reviewed_by: currentUserId,
        comment: comment,
      };

      await api.put(`/architectural-requests/${architecturalRequestDetails.id}`, updatedData);

      onUpdate(updatedData);
      alert("Architectural request updated successfully!");
      handleEditModalClose();
    } catch (error) {
      console.error("Error updating request:", error);
      alert("Failed to update request.");
    } finally {
      setIsSaving(false);
    }
  };

  const currentStatusObj = statuses.find(
    (s) => s.statusname.toLowerCase() === updatedStatus.toLowerCase()
  );

  return (
    <>
      {/* Status Icon */}
      <Box sx={{ textAlign: "center", my: 2 }}>
        {updatedStatus === "pending" && (
          <PendingActionsIcon sx={{ color: currentStatusObj?.color || "#ffc107", fontSize: 40 }} />
        )}
        {updatedStatus === "approved" && (
          <CheckCircleOutlineIcon sx={{ color: currentStatusObj?.color || "#4caf50", fontSize: 40 }} />
        )}
        {updatedStatus === "rejected" && (
          <CancelIcon sx={{ color: currentStatusObj?.color || "#f44336", fontSize: 40 }} />
        )}
      </Box>

      {/* Modal */}
      <Modal open={isEditModalOpen} onClose={handleEditModalClose}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 400,
            backgroundColor: "white",
            padding: 4,
            borderRadius: 2,
            boxShadow: 24,
          }}
        >
          <Typography variant="h6" sx={{ mb: 2 }}>
            Edit Request
          </Typography>

          {/* Status Dropdown */}
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Status</InputLabel>
            <Select value={updatedStatus} onChange={(e) => setUpdatedStatus(e.target.value)}>
              {statuses.map((status) => (
                <MenuItem key={status.id} value={status.statusname}>
                  {status.statusname}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Comment Input */}
          <TextField
            fullWidth
            label="Comment"
            multiline
            rows={3}
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            sx={{ mb: 2 }}
          />

          {/* Save Button */}
          <Button
            variant="contained"
            color="primary"
            onClick={handleSaveChanges}
            fullWidth
            disabled={isSaving}
          >
            {isSaving ? "Saving..." : "Save Changes"}
          </Button>
        </Box>
      </Modal>
    </>
  );
});

export default EditArchitecturalRequest;
